

import { useState } from "react";
import { SMTPSettings } from "@/lib/settings";

interface EmailSettingsSectionProps {
  data: SMTPSettings;
  onChange: (data: SMTPSettings) => void;
  onSave: () => void;
  onTest: (testEmail: string) => Promise<{ success: boolean; message: string }>;
  saving?: boolean;
  readOnly?: boolean;
}

export default function EmailSettingsSection({
  data,
  onChange,
  onSave,
  onTest,
  saving = false,
  readOnly = false,
}: EmailSettingsSectionProps) {
  const [smtpTestEmail, setSmtpTestEmail] = useState("");
  const [smtpTestStatus, setSmtpTestStatus] = useState<"idle" | "testing" | "success" | "error">("idle");
  const [smtpTestMessage, setSmtpTestMessage] = useState("");

  const handleTest = async () => {
    if (!smtpTestEmail) {
      return;
    }
    setSmtpTestStatus("testing");
    try {
      const result = await onTest(smtpTestEmail);
      // onTest returns a promise that resolves to { success, message }
      // but we need to handle it properly
      setSmtpTestStatus(result.success ? "success" : "error");
      setSmtpTestMessage(result.message);
    } catch (error: any) {
      setSmtpTestStatus("error");
      setSmtpTestMessage(error.message || "Test failed");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-stone-800 font-serif">Email / SMTP Settings</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-500">Enable Email</span>
          <button
            onClick={() => onChange({ ...data, enabled: !data.enabled })}
            disabled={readOnly}
            className={`w-10 h-5 rounded-full transition-colors relative ${
              data.enabled ? "bg-emerald-500" : "bg-stone-300"
            } ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
              data.enabled ? "left-5" : "left-0.5"
            }`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">SMTP Host *</label>
          <input
            type="text"
            value={data.host}
            onChange={(e) => onChange({ ...data, host: e.target.value })}
            disabled={readOnly}
            placeholder="smtp.yourprovider.com"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
          <p className="text-xs text-stone-400 mt-1">Example: smtp.gmail.com, mail.yourdomain.com</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Port *</label>
          <select
            value={data.port}
            onChange={(e) => onChange({ ...data, port: parseInt(e.target.value) })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          >
            <option value={587}>587 (STARTTLS)</option>
            <option value={465}>465 (SSL/TLS)</option>
            <option value={25}>25 (Unencrypted)</option>
            <option value={2525}>2525 (Alternative)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Security</label>
          <select
            value={data.secure ? "true" : "false"}
            onChange={(e) => onChange({ ...data, secure: e.target.value === "true" })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          >
            <option value="false">STARTTLS (Port 587)</option>
            <option value="true">SSL/TLS (Port 465)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Username / Email *</label>
          <input
            type="text"
            value={data.user}
            onChange={(e) => onChange({ ...data, user: e.target.value })}
            disabled={readOnly}
            placeholder="your@email.com"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Password *</label>
          <input
            type="password"
            value={data.pass}
            onChange={(e) => onChange({ ...data, pass: e.target.value })}
            disabled={readOnly}
            placeholder="••••••••"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
          <p className="text-xs text-stone-400 mt-1">Use app password for Gmail</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">From Name</label>
          <input
            type="text"
            value={data.fromName}
            onChange={(e) => onChange({ ...data, fromName: e.target.value })}
            disabled={readOnly}
            placeholder="Shalimar Banquet Hall"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">From Email *</label>
          <input
            type="email"
            value={data.fromEmail}
            onChange={(e) => onChange({ ...data, fromEmail: e.target.value })}
            disabled={readOnly}
            placeholder="noreply@shalimar.in"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
        </div>
      </div>

      {/* Test Email Section */}
      <div className="mt-6 pt-6 border-t border-stone-100">
        <h3 className="text-sm font-semibold text-stone-800 mb-4">Test Email Configuration</h3>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Test Email Address</label>
            <input
              type="email"
              value={smtpTestEmail}
              onChange={(e) => setSmtpTestEmail(e.target.value)}
              disabled={readOnly}
              placeholder="Enter email to send test"
              className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleTest}
              disabled={!data.host || !data.user || !data.pass || !data.fromEmail || !smtpTestEmail || readOnly}
              className="px-6 py-2.5 rounded-xl border border-stone-200 text-stone-600 font-semibold text-sm hover:bg-stone-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {readOnly ? "View Only" : "Send Test Email"}
            </button>
          </div>
        </div>
        <p className="text-xs text-stone-400 mt-2">Send a test email to verify your SMTP configuration is working correctly.</p>
      </div>

      {/* Test Connection Status */}
      {smtpTestStatus !== "idle" && (
        <div className={`mt-4 p-4 rounded-xl ${
          smtpTestStatus === "success" ? "bg-emerald-50 text-emerald-700" :
          smtpTestStatus === "error" ? "bg-rose-50 text-rose-700" :
          "bg-amber-50 text-amber-700"
        }`}>
          <div className="flex items-center gap-2">
            {smtpTestStatus === "testing" ? (
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : smtpTestStatus === "success" ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className="font-medium">
              {smtpTestStatus === "testing" ? "Testing connection..." : smtpTestMessage}
            </span>
          </div>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-stone-100">
        <button
          onClick={onSave}
          disabled={saving || readOnly}
          className="px-6 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? "Saving..." : readOnly ? "View Only" : "Save SMTP Settings"}
        </button>
      </div>
    </div>
  );
}
